<template>
  <div class="nebMultiValueInput"
    v-bind="$attrs"
    :style="{
      width
    }"
    @keyup="keyupHandler"
  >
    <div class="nebMultiValueInput-box"
      :style="{
        minHeight
      }"
    >
      <neb-pill v-for="(val, idx) in preppedValue"
        :key="idx"
        :text="val"
        :closable="!readOnly"
        @close="() => onRemoveValue(val)"
      ></neb-pill>

      <div class="nebMultiValueInput-controls"
        v-if="!readOnly"
      >
        <input type="text"
          class="nebMultiValueInput-input"
          :placeholder="placeholder || ''"
          v-model="formData.text"
          @keyup.enter="onConfirmInput"
          @blur="onInputBlur"
          ref="inputBox"
        >

        <div class="nebMultiValueInput-optionList"
          v-if="isSelect"
          v-show="showOptionsList"
          v-loading="optionsLoading"
          :class="optionListDirectionClass"
        >
          <div class="nebMultiValueInput-option"
            v-if="!optionsLoading && options.length < 1"
          >
            No Results
          </div>

          <div class="nebMultiValueInput-option"
            v-for="(opt, idx) in options"
            :key="idx"
            @click="() => onSelect(opt)"
          >
            {{ opt.label }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { clone } from 'lodash'

import NebPill from '../Pill'

export default {
  /**
   * @event NebMultiValueInput#textChange
   * @description Fired when a user types in the input box.
   * @type {String} - The value of the text input.
   */
  name: 'NebMultiValueInput',

  components: {
    NebPill
  },

  props: {
    value: {
      type: Array[String | Number],
      default: () => []
    },
    /**
     * @param {Object[]} options
     * @param {String} options.label - Option label.
     * @param {String|Number} options.value - Option Value.
     * @description An array of option objects to populate the select dropdown.
     */
    options: {
      type: Array[Object],
      default: null
    },
    minHeight: {
      type: String,
      default: '100px'
    },
    width: {
      type: String,
      default: '100%'
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    },
    /**
     * @param {Boolean} isSelect
     * @description Whether or not the input should be treated as a searchable select. When true, a select dropdown
     * will be rendered with the options passed in the `options` parameter.
     */
    isSelect: {
      type: Boolean,
      default: false
    },
    /**
     * Whether or not to allow freeform text input. Intended to be used alongside `isSelect = true` to restrict user
     * to only be able to pick from available options. If `isSelect = true` and `allowFreeform = true`, users will
     * be able to pick from a list of options OR submit their freeform text.
     */
    allowFreeform: {
      type: Boolean,
      default: true
    },
    /**
     * @param {Boolean} optionsLoading
     * @description Indicates whether the options dropdown should show a loading indicator.
     */
    optionsLoading: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    optionsOpen: false,
    formData: {
      text: ''
    },
    justAddedValue: null,
    optionListDirectionClass: 'down'
  }),

  computed: {
    preppedValue () {
      const items = clone(this.value)

      return items
        .sort(item => item === this.justAddedValue ? 1 : -1)
    },
    showOptionsList () {
      return this.optionsOpen &&
        this.isSelect &&
        this.options.length > 0
    }
  },

  methods: {
    keyupHandler (e) {
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          e.stopPropagation()
      }
    },
    onConfirmInput () {
      if (!this.allowFreeform) {
        return
      }

      const { text } = this.formData

      this.$emit('input', this.value.concat([text]))
      this.justAddedValue = clone(text)
      this.formData.text = ''
    },
    onSelect (opt) {
      this.optionsOpen = false
      this.justAddedValue = opt.label
      this.formData.text = ''
      this.$emit('input', this.value.concat([opt.value]))
    },
    onRemoveValue (val) {
      const newValue = this.value
        .filter(value => value !== val)

      this.$emit('input', newValue)
    },
    onInputBlur () {
      // Delay closing the dropdown so the dropdown option click event can be handled before the dropdown is hidden.
      setTimeout(() => {
        this.optionsOpen = false
        this.formData.text = ''
      }, 100)
    },
    onOptionListUnderlayClick () {
      this.optionsOpen = false
      this.formData.text = ''
    }
  },

  watch: {
    'formData.text' (newVal) {
      if (newVal.length > 0 && this.isSelect) {
        this.optionsOpen = true
      }

      this.$emit('textChange', newVal)
    }
  },

  updated () {
    if (!this.readOnly) {
      const distanceToBottom = window.innerHeight -
        this.$refs.inputBox.getBoundingClientRect().bottom

      this.optionListDirectionClass = distanceToBottom > 200 ? 'down' : 'up'
    }

    this.labelList = this.preppedValue
  }
}
</script>

<style lang="scss">
.nebMultiValueInput {
  &-box {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 10px 15px;
    border: solid 1px #a0c3d2;
    border-radius: 3px;

    span:not(.nebPill-close) {
      margin-right: 10px;
      margin-bottom: 10px;
    }
  }

  &-controls {
    position: relative;
    padding: 6px 12px 6px 0;
    flex-grow: 1;

    input {
      width: 100%;
      font-size: 12px;

      &:focus {
        outline: none;
      }

      &::placeholder {
        color: #9b9b9b;
      }
    }
  }

  &-optionList {
    position: absolute;
    background-color: #fff;
    z-index: 999;
    border-radius: 3px;
    border: solid 1px #cecece;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    min-width: 150px;
    min-height: 30px;
    max-height: 200px;
    overflow-x: visible;
    overflow-y: scroll;

    &.down {
      top: 22px;
      left: 0;
    }

    &.up {
      bottom: 22px;
      left: 0;
    }
  }

  &-option {
    font-size: 12px;
    color: #5c5e62;
    line-height: 2.5;
    cursor: pointer;
    padding: 0 15px;
    min-width: 100px;
    box-sizing: border-box;
    overflow: hidden;
    white-space: nowrap;

    &:hover {
      background-color: #e8f4fc;
    }
  }
}
</style>
